import Button from "devextreme-react/button";
import { confirm } from "devextreme/ui/dialog";
import React from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { DefaultStepDto } from "../../../dtos/DefaultStepDto";
import { GrantDto } from "../../../dtos/GrantDto";
import { PhaseDto } from "../../../dtos/PhaseDto";
import { UserRole } from "../../../dtos/UserRole";
import { PermissionType } from "../../../dtos/PermissionType";
import { StepStatus } from "../../../dtos/StepStatus";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import TaskSummary from "../../shared/tasklist/tasks/TaskSummary";
import { mapDefaultPhaseToPhaseDto } from "../../shared/tasklist/tasks/adapters/templateMappers";

type Props = {
    task?: DefaultPhaseDto | null;
    subtask?: DefaultStepDto | null;
    grantData: GrantDto;
    configOptions?: WorkflowConfigOptionsDto | null;
    onClose: () => void;
    onDelete?: () => Promise<void>;
};

// Helper to get role label
const getRoleLabel = (role: UserRole | null | undefined): string => {
    if (role === null || role === undefined) return "Not set";
    const roleLabels: Partial<Record<UserRole, string>> = {
        [UserRole.PO]: "Program Officer",
        [UserRole.GMS]: "Grants Management Specialist",
        [UserRole.SU]: "Super User",
        [UserRole.PI]: "Principal Investigator",
        [UserRole.SO]: "Signing Official",
        [UserRole.ORF_DTR]: "ORF-DTR",
        [UserRole.ORF_DEP]: "ORF-DEP",
        [UserRole.PO_SUPERVISOR]: "Program Officer (Supervisor)",
        [UserRole.GMS_SUPERVISOR]: "Grants Management Specialist (Supervisor)",
        [UserRole.ORF_DTR_SUPERVISOR]: "ORF-DTR (Supervisor)",
        [UserRole.EXTERNAL_USER]: "External User",
        [UserRole.EXTERNAL_REVIEWER]: "Reviewer",
        [UserRole.EXTERNAL_AE_DESIGNER]: "A/E Designer",
        [UserRole.EXTERNAL_PROJECT_MANAGER]: "Project Manager",
    };
    return roleLabels[role] || "Not set";
};

// Helper to get permission label
const getPermissionLabel = (permission: PermissionType): string => {
    const labels: Record<PermissionType, string> = {
        [PermissionType.View]: "View",
        [PermissionType.Delete]: "Delete",
        [PermissionType.Edit]: "Edit",
        [PermissionType.EditDates]: "Edit Dates",
        [PermissionType.EditStartDate]: "Edit Start Date",
        [PermissionType.EditEndDate]: "Edit End Date",
        [PermissionType.EditCompletionDate]: "Edit Completion Date",
        [PermissionType.Upload]: "Upload",
        [PermissionType.Comment]: "Comment",
        [PermissionType.EditRequest]: "Edit Request",
        [PermissionType.EditResponse]: "Edit Response",
        [PermissionType.Resubmit]: "Resubmit",
    };
    return labels[permission] || String(permission);
};

// Helper to get status label - uses configOptions if available, falls back to defaults
const getStatusLabel = (status: StepStatus, configOptions?: WorkflowConfigOptionsDto | null): string => {
    // Use config options from backend if available (single source of truth)
    if (configOptions?.subtask?.statuses) {
        const statusOption = configOptions.subtask.statuses.find(s => s.value === status);
        if (statusOption) return statusOption.label;
    }

    // Fallback to hardcoded labels (should match C# EnumMetadata)
    const labels: Record<StepStatus, string> = {
        [StepStatus.NOT_STARTED]: "Not Started",
        [StepStatus.SETUP]: "In Progress - NIH",
        [StepStatus.READY]: "Ready - NIH",
        [StepStatus.GRANTEE_PENDING]: "In Progress - Grantee",
        [StepStatus.GRANTEE_PENDING_REVISION]: "Need Information - Grantee",
        [StepStatus.GRANTEE_COMPLETE]: "Complete - Grantee",
        [StepStatus.PENDING]: "Pending Approval - NIH",
        [StepStatus.PENDING_REVISION]: "Need Information - Grantee",
        [StepStatus.APPROVED]: "Approved",
        [StepStatus.INACTIVE]: "Inactive",
    };
    return labels[status] || String(status);
};

export default function TemplateSummaryTab({ task, subtask, grantData, configOptions, onClose, onDelete }: Props) {
    const handleDeleteClick = async () => {
        if (!onDelete || !subtask) return;

        const result = await confirm(
            `Are you sure you want to delete the subtask "${subtask.name}"? This action cannot be undone if the subtask has no instances.`,
            "Confirm Deletion"
        );

        if (!result) return;
        await onDelete();
    };
    // For tasks - show the TaskSummary component (subtask list)
    if (task) {
        const phase: PhaseDto = mapDefaultPhaseToPhaseDto(task);
        return (
            <div style={{ padding: "0", height: "100%", minHeight: "400px" }}>
                <TaskSummary
                    phase={phase}
                    grantNumber={grantData.grantNumber}
                    grantId={grantData.id}
                    hideSummary={false}
                    onClose={onClose}
                />
            </div>
        );
    }

    // For subtasks - show a readable summary of the configuration
    if (subtask) {
        const settings = subtask.settings;
        const workflow = settings?.workflow;
        const permissions = workflow?.permissions;
        const transitions = workflow?.transitions;

        return (
            <div style={{ padding: "16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h5 style={{ margin: 0 }}>{subtask.name}</h5>
                    {onDelete && (
                        <Button
                            icon="trash"
                            text="Delete Subtask"
                            type="danger"
                            stylingMode="contained"
                            onClick={handleDeleteClick}
                        />
                    )}
                </div>

                {subtask.description && (
                    <div style={{ marginBottom: "16px" }}>
                        <strong>Description:</strong>
                        <p style={{ margin: "4px 0 0 0", color: "#555" }}>{subtask.description}</p>
                    </div>
                )}

                {/* Permissions Summary - only show roles that have permissions configured */}
                {permissions?.roleConfigs && permissions.roleConfigs.filter(rc =>
                    rc.permissions && rc.permissions.length > 0 &&
                    rc.permissions.some(p => p.statuses && p.statuses.length > 0)
                ).length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                        <strong>Permissions:</strong>
                        <div style={{ marginTop: "8px", fontSize: "14px" }}>
                            {permissions.roleConfigs
                                .filter(rc => rc.permissions && rc.permissions.length > 0 &&
                                    rc.permissions.some(p => p.statuses && p.statuses.length > 0))
                                .map((rc, idx) => (
                                <div key={idx} style={{ marginBottom: "8px", padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                    <div style={{ fontWeight: 500 }}>{getRoleLabel(rc.role)}</div>
                                    {rc.permissions?.filter(p => p.statuses && p.statuses.length > 0).map((p, pIdx) => (
                                        <div key={pIdx} style={{ marginLeft: "12px", color: "#666" }}>
                                            • {getPermissionLabel(p.permission)}: {p.statuses?.map(s => getStatusLabel(s as StepStatus, configOptions)).join(", ") || "None"}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Transitions Summary */}
                {transitions?.transitions && transitions.transitions.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                        <strong>Transitions:</strong>
                        <div style={{ marginTop: "8px", fontSize: "14px" }}>
                            {transitions.transitions.map((t, idx) => (
                                <div key={idx} style={{ marginBottom: "4px", padding: "8px", backgroundColor: "#f5f5f5", borderRadius: "4px" }}>
                                    <span style={{ fontWeight: 500 }}>{getStatusLabel(t.from as StepStatus, configOptions)} → {getStatusLabel(t.to as StepStatus, configOptions)}</span>
                                    <span style={{ marginLeft: "8px", color: "#666" }}>
                                        ({t.roles?.map(r => getRoleLabel(r)).join(", ") || "No roles"})
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Show message if no workflow config */}
                {!permissions?.roleConfigs?.length && !transitions?.transitions?.length && (
                    <div style={{ color: "#888", fontStyle: "italic" }}>
                        No workflow configuration set. Using defaults.
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="template-tab">
            <div className="template-tab-body" style={{ color: "#666" }}>
            <p>Select a task or subtask to view its summary.</p>
    </div>
        </div>
    );
}
